import { Group } from 'src/design/atoms/Group';
import { Fieldset } from 'src/design/atoms/Fieldset';
import { Tabs } from 'src/design/molecules/Tabs';
import React, { useState } from 'react';
import { WorkflowStepDto } from 'src/api/FiretrolApiClient';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { EditDrawerApprovalTab } from './EditDrawerApprovalTab';
import { EditDrawerNotificationsTab } from './EditDrawerNotificationsTab';
import { EditDrawerSettingsTab } from './EditDrawerSettingsTab';

export type { WorkflowStepDto };

export interface IEditDrawerProps {
  step: WorkflowStepDto;
  locked: boolean;
  onValueChange: any;
  onStepRemoved: () => void;
  isTemplateEditor: boolean;
  resourceId?: string;
  readOnly?: boolean;
}

/** @registry Tabbed settings editor for a single workflow step — general settings, notifications, and approval. */
export const EditDrawer: React.FC<IEditDrawerProps> = ({
  step,
  locked,
  onValueChange,
  onStepRemoved,
  isTemplateEditor,
  resourceId,
  readOnly = false,
}) => {
  const [tab, setTab] = useState('settings');

  if (!step) return null;

  const removeStep = async () => {
    onStepRemoved();
  };

  const onFieldChange = (field: string, value: any) => {
    onValueChange({
      ...step,
      [field]: value,
    });
  };

  const onButtonTextUpdated = (val: any) => {
    onFieldChange('buttonText', val);
  };

  return (
    <Tabs value={tab} onChange={setTab}>
      <Tabs.List>
        <Tabs.Tab value="settings">General Settings</Tabs.Tab>
        <Tabs.Tab value="notifications">Notifications</Tabs.Tab>
        {!step.isEmployeeReviewStep && !step.isSupervisorReviewStep && (
          <Tabs.Tab value="approval">Approval</Tabs.Tab>
        )}
      </Tabs.List>

      <Fieldset disabled={readOnly} variant="unstyled" p={0}>
        <Tabs.Panel value="settings">
          <EditDrawerSettingsTab
            current={step}
            isTemplateEditor={isTemplateEditor}
            resourceId={resourceId}
            onFieldChange={onFieldChange}
            onValueChange={onValueChange}
            onButtonTextUpdated={onButtonTextUpdated}
          />
        </Tabs.Panel>

        <Tabs.Panel value="notifications">
          <EditDrawerNotificationsTab
            current={step}
            isTemplateEditor={isTemplateEditor}
            onFieldChange={onFieldChange}
          />
        </Tabs.Panel>

        <Tabs.Panel value="approval">
          <EditDrawerApprovalTab
            current={step}
            isTemplateEditor={isTemplateEditor}
            onFieldChange={onFieldChange}
          />
        </Tabs.Panel>
      </Fieldset>

      {!locked && !readOnly && (
        <Group justify="flex-start" mt="lg">
          <InlineConfirmButton
            color="red"
            confirmButtonProps={{
              children: 'Confirm Remove',
            }}
            onConfirmClick={removeStep}
          >
            Remove Step
          </InlineConfirmButton>
        </Group>
      )}
    </Tabs>
  );
};
